import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { Pencil, Trash2, Plus } from 'lucide-react'

import confirm from 'components/dialogConfirm'
import FbButton from 'components/FbUI/FbButton'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import {
  type SenderEmail,
  useDeleteSenderEmail,
  useGetWorkspaceSenderEmails,
  useSyncWorkspaceSenderEmails,
} from 'features/campaigns/sender_emails_api'
import { useGetHeadlessUIToken } from 'features/campaigns/headless_ui_api'
import { SenderEmailsTable } from 'features/campaigns/SequenceTab/SenderEmailsTable'
import { EditSenderEmailModal } from './EditSenderEmailModal'
import * as SettingsS from './styles'
import * as S from '../../../components/Tables/CommonTable.styles'

const DESCRIPTION_TEXT =
  'Manage your sender email accounts. Sync from EmailBison to keep your sender emails up to date.'

export function SenderEmailSettings() {
  const [editingSenderEmail, setEditingSenderEmail] =
    useState<SenderEmail | null>(null)

  const {
    data: senderEmails,
    isLoading,
    isFetching,
  } = useGetWorkspaceSenderEmails()

  const syncMutation = useSyncWorkspaceSenderEmails()
  const deleteMutation = useDeleteSenderEmail()
  const headlessTokenMutation = useGetHeadlessUIToken()

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync()
      toast.success(
        `Synced: ${result.created} added, ${result.updated} updated`
      )
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Failed to sync sender emails'
      toast.error(errorMessage)
    }
  }

  const handleDelete = useCallback(
    async (senderEmail: SenderEmail) => {
      const confirmed = await confirm(
        `Are you sure you want to delete "${senderEmail.name || senderEmail.email}"?`,
        'Delete Sender Email',
        'This will permanently remove the sender email from EmailBison. This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )

      if (!confirmed) return

      try {
        await deleteMutation.mutateAsync(senderEmail.id)
        toast.success('Sender email deleted successfully')
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || 'Failed to delete sender email'
        toast.error(errorMessage)
      }
    },
    [deleteMutation]
  )

  const getRowActions = useCallback(
    (senderEmail: SenderEmail) => [
      {
        label: 'Edit',
        icon: Pencil,
        onClick: () => setEditingSenderEmail(senderEmail),
      },
      {
        label: 'Delete',
        icon: Trash2,
        onClick: () => handleDelete(senderEmail),
        variant: 'destructive' as const,
        disabled: deleteMutation.isPending,
      },
    ],
    [handleDelete, deleteMutation.isPending]
  )

  const tableHeader = (
    <>
      <SettingsS.SettingsTableHeader>
        <OverflownTextTooltip
          tooltipText={DESCRIPTION_TEXT}
          maxLines={4}
          placement="bottom"
        >
          <SettingsS.TableDescriptionText>
            {DESCRIPTION_TEXT}
          </SettingsS.TableDescriptionText>
        </OverflownTextTooltip>
      </SettingsS.SettingsTableHeader>
      <S.SearchContainer>
        <S.SearchContainerFilters>
          <div />
          <S.TableButtonsContainer>
            <FbButton
              variant="secondary"
              size="sm"
              onClick={handleSync}
              disabled={syncMutation.isPending}
              loading={syncMutation.isPending}
            >
              Sync from EmailBison
            </FbButton>
            <FbButton
              variant="primary"
              size="sm"
              disabled={headlessTokenMutation.isPending}
              loading={headlessTokenMutation.isPending}
              onClick={async () => {
                try {
                  const { token } = await headlessTokenMutation.mutateAsync()
                  const addEmailUrl = `https://outbound.firstbite.io/headless-ui-login?token=${token}`

                  const width = 600
                  const height = 700
                  const left = (screen.width - width) / 2
                  const top = (screen.height - height) / 2
                  const popup = window.open(
                    addEmailUrl,
                    'AddEmailConnection',
                    `width=${width},height=${height},left=${left},top=${top}`
                  )

                  // Poll to check when popup is closed, then sync
                  if (popup) {
                    const checkClosed = setInterval(() => {
                      if (popup.closed) {
                        clearInterval(checkClosed)
                        handleSync()
                      }
                    }, 500)
                  }
                } catch (error: any) {
                  const errorMessage =
                    error.response?.data?.detail ||
                    'Failed to fetch authentication token'
                  toast.error(errorMessage)
                }
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Email Connection
            </FbButton>
          </S.TableButtonsContainer>
        </S.SearchContainerFilters>
      </S.SearchContainer>
    </>
  )

  return (
    <>
      <SenderEmailsTable
        data={senderEmails?.results ?? []}
        isLoading={isLoading || isFetching}
        tableKey="settings-sender-emails-table"
        tableHeader={tableHeader}
        getRowActions={getRowActions}
      />
      <EditSenderEmailModal
        isOpen={!!editingSenderEmail}
        onClose={() => setEditingSenderEmail(null)}
        senderEmail={editingSenderEmail}
      />
    </>
  )
}
